import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input
} from '@angular/core';
import { CommonChartComponent } from '../common-chart/common-chart.component';
import { ChartsCommonService } from '../../../services/charts-common.service';
import {
  Axis,
  AxisTickStrategies,
  LineSeries,
  SeriesXY,
  UIElementBuilders
} from '@arction/lcjs';
import {
  AxisXMaturity,
  IMappedSeriesPointsResponse,
  Point
} from 'app/charts-dashboard/interfaces/chart-http.interface';
import {
  ChartsDashboardSeriesQuery,
  IChartSeries,
  SeriesTypeEnum
} from '../../../state/series';
import { ChartsDashboardQuery } from '../../../state/dashboard';
import { ChartsDashboardCardQuery } from '../../../state/card';
import { CommonService } from 'app/core/services/common.service';
import { zip } from 'rxjs';
import { ChartsHttpService } from '../../../services/charts-http.service';
import {
  Datetime,
  IChartFilter,
  IHistoricalPointsData,
  ILCPoint,
  RealTimeEnum
} from '../../../interfaces';
import * as moment from 'moment';
import {
  getNYTime,
  momentDateFormat,
  momentDateTimeFormat
} from '../../../helpers/charts-module-helpers';
import {
  EntityActions,
  ID
} from '@datorama/akita';
import { ChartsDashboardStateService } from '../../../state/charts-dashboard-state.service';
import {
  skip,
  takeUntil
} from 'rxjs/operators';

@Component({
  selector: 'iv-live-maturity-chart',
  template: '<ng-content></ng-content>',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MaturityChartComponent extends CommonChartComponent {

  constructor(
    protected readonly commonService: CommonService,
    protected readonly chartsCommonService: ChartsCommonService,
    protected readonly elementRef: ElementRef,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly dashboardQuery: ChartsDashboardQuery,
    protected readonly seriesQuery: ChartsDashboardSeriesQuery,
    protected readonly cardsQuery: ChartsDashboardCardQuery,
    protected readonly httpService: ChartsHttpService,
    protected readonly chartsState: ChartsDashboardStateService
  ) {
    super(
      commonService,
      chartsCommonService,
      elementRef,
      changeDetectorRef,
      dashboardQuery,
      seriesQuery,
      cardsQuery,
      chartsState
    );
  }

  @Input()
  public skipSettingTimestamp: boolean;

  private skip: { [seriesId: string]: boolean };

  protected loadSeriesPoints(seriesIds: ID[], isFirstLoad?: boolean): void {
    const series: IChartSeries[] = this.seriesQuery.getAll().filter((_series: IChartSeries) => seriesIds.find(id => id === _series.id));

    zip(...this.createSeriesObservables(series, isFirstLoad))
      .subscribe((res: IMappedSeriesPointsResponse<AxisXMaturity>[]) => {
        res.forEach((pointsData: IMappedSeriesPointsResponse<AxisXMaturity>) => {
          if (isFirstLoad && this.chart.getDefaultAxisX().getTickStrategy() === 'Numeric' && pointsData.seriesId === this.mainSeries.id) {
            this.setTickStrategy(pointsData.points);
          }
          this.fillLineSeries(pointsData, this.mainSeries?.id === pointsData.seriesId, isFirstLoad);

          if (this.skipSettingTimestamp === false) {
            this.setTimestamp(pointsData);
          }

          this.pointsData = {
            ...this.pointsData,
            [pointsData.seriesId]: pointsData
          };
        });
      });
  }

  protected loadAllSeriesPoints(isFirstLoad?: boolean): void {
    zip(...this.createSeriesObservables(this.allSeries, true))
      .subscribe((res: IMappedSeriesPointsResponse<AxisXMaturity>[]) => {
        if (isFirstLoad) {
          this.setTickStrategy(
            res.find((pointsData: IMappedSeriesPointsResponse<AxisXMaturity>) => pointsData.seriesId === this.mainSeries.id).points
          );
          this.setCursorLegendAxis(res[0]);
        }

        res.forEach((pointsData: IMappedSeriesPointsResponse<AxisXMaturity>, index: number) => {
          this.skip = {...this.skip, [pointsData.seriesId]: false};
          this.fillLineSeries(pointsData, !index, isFirstLoad);
          this.setTimestamp(pointsData);
          this.pointsData = {
            ...this.pointsData,
            [pointsData.seriesId]: pointsData
          };
        });
      });
  }

  private setTimestamp(pointsData: IMappedSeriesPointsResponse<AxisXMaturity>): void {
    if (!pointsData.timestamp) return;

    this.skip[pointsData.seriesId] = true;
    this.chartsState.updateMaturityTimestamp(
      pointsData.seriesId,
      moment(new Date(pointsData.timestamp), 'MM-DD-YYYY HH:mm:ss A').format(momentDateTimeFormat) as Datetime
    );
  }

  protected setTickStrategy(mainSeriesPoints: Point<AxisXMaturity>[]): void {
    if (!mainSeriesPoints.length) return;

    const axisX: Axis = this.chart
      .getDefaultAxisX()
      .setTickStrategy(AxisTickStrategies.Empty);

    mainSeriesPoints.forEach((point: Point<number>, index: number) => {
      axisX
        .addCustomTick(UIElementBuilders.AxisTick)
        .setTextFormatter(() => point.x.toString())
        .setValue(index)
        .setMarker(marker => marker.setTextFont(font => font.setSize(12)))
        .setTickLength(4)
        .setGridStrokeStyle((styles: any) => styles.setFillStyle(fill => fill.setA(20)));
    });
  }

  protected setCursorResultTableFormatter(lineSeries: LineSeries, series: IChartSeries, filters?: IChartFilter[]): void {
    if (!lineSeries) return;

    lineSeries
      .setCursorResultTableFormatter((builder, _series: SeriesXY, x: number, y: number, dataPoint: ILCPoint) => {
        this.builderCursorResult = builder;
        this.builderCursorResult
          .addRow(`${moment(getNYTime(series.selectedFilters.datetime), 'MM-DD-YYYY HH:mm:ss A').format(momentDateFormat)}`)
          .addRow(`${this.cursorResult.x}: ${dataPoint.additional.x.value}`)
          .addRow(`${this.cursorResult.y}: ${_series.axisY.formatValue(y)}`)
          .addRow(`${this.cursorResult.z}: ${dataPoint.z}`);

        if (filters?.length) {
          filters.forEach((filter: IChartFilter) => {
            this.builderCursorResult.addRow(`${filter.name}: ${series.selectedFilters[filter.parameterId].description}`);
          });
        }

        return this.builderCursorResult;
      });
  }

  protected mapPoints(pointsData: IMappedSeriesPointsResponse<AxisXMaturity>): ILCPoint[] {
    return pointsData.points.map((item: Point<AxisXMaturity>, index: number) => {
      return {x: index, y: +item.y, z: item?.z, additional: {x: {value: +item.x}}};
    });
  }

  protected addPointsFromRt(data: IMappedSeriesPointsResponse<AxisXMaturity>): void {
    this.fillLineSeries(data, this.seriesQuery.getEntity(data.seriesId).type === SeriesTypeEnum.MAIN);
    this.setTimestamp(data);
    this.pointsData = {
      ...this.pointsData,
      [data.seriesId]: {...this.pointsData[data.seriesId], points: [...(this.pointsData as IHistoricalPointsData)[data.seriesId]?.points, ...data.points]}};
  }

  protected seriesSubscription(): void {
    super.seriesSubscription();
    this.seriesQuery.selectEntityAction(EntityActions.Update)
      .pipe(
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe((ids: ID[]) => {
        if (this.skip && this.skip[ids[0]]) {
          this.skip[ids[0]] = false;
          return;
        }

        if (!!ids.filter(id => this.card.chartSeries.includes(id)).length) {
          this.loadSeriesPoints(ids, true);
        }

        if (this.mainSeries.selectedFilters.realtime?.name === RealTimeEnum.ON) {
          this.startRt();
        } else if (this.rtSubscription) {
          this.rtSubscription.unsubscribe();
          this.isRtActive = false;
        }
      });
  }
}
